import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NoticeRequest, NoticeTableResponse, NoticeTranslationResponse, SettingsService } from '@src/app/shared/backend/clients';
import { NoticePlacement } from '@src/app/shared/enums/NoticePlacement.enum';
import { NoticeType } from '@src/app/shared/enums/NoticeType.enum';
import { SessionService } from '@src/app/shared/services/session.service';
import { firstValueFrom } from 'rxjs';

interface NoticeDropdownOption {
  id: number;
  text: string;
}

interface NoticeEditorLanguage {
  code: string;
  name: string;
}

@Component({
  selector: 'app-notices-settings',
  templateUrl: './notices-settings.component.html',
  styleUrls: ['./notices-settings.component.scss'],
  host: {
    class: 'app-notices-settings'
  },
  standalone: false
})
export class NoticesSettingsComponent implements OnInit {

  paginatedNotices: NoticeTableResponse[] = [];

  paginatorFirst: number = 0;
  paginatorRows: number = 10;
  totalRecords: number = 0;

  loading = false;
  updatingNoticeId: number | null = null;

  showNoticeModalbox = false;
  openingNoticeModal = false;
  savingNotice = false;
  selectedNoticeId: number | null = null;
  showDeleteModal = false;
  currentNoticeName: any = '';
  currentNotice: any = { isActive: true };

  enabledLanguages: NoticeEditorLanguage[] = [];
  availablePlacementOptions: NoticeDropdownOption[] = [];
  existingTranslations: NoticeTranslationResponse[] = [];

  noticeTypeLabels: Record<number, string> = {
    [NoticeType.INFORMATION]: 'SYSTEM_SETTINGS.NOTICES.TYPE_INFORMATION',
    [NoticeType.WARNING]: 'SYSTEM_SETTINGS.NOTICES.TYPE_WARNING',
    [NoticeType.ALERT]: 'SYSTEM_SETTINGS.NOTICES.TYPE_ALERT'
  };

  noticePlacementLabels: Record<number, string> = {
    [NoticePlacement.SALE_ADD_PRODUCT_IDENTIFIER_SN]:
      'SYSTEM_SETTINGS.NOTICES.PLACEMENT_SALE_IDENTIFIER',
    [NoticePlacement.WARRANTY_ACTIVATION_IDENTIFIER_SN]:
      'SYSTEM_SETTINGS.NOTICES.PLACEMENT_WARRANTY_IDENTIFIER',
    [NoticePlacement.DIGITAL_PASSPORT_HEADER]:
      'SYSTEM_SETTINGS.NOTICES.PLACEMENT_DIGITAL_PASSPORT_HEADER',
    [NoticePlacement.DIGITAL_PASSPORT_FOOTER]:
      'SYSTEM_SETTINGS.NOTICES.PLACEMENT_DIGITAL_PASSPORT_FOOTER'
  };

  noticeTypeOptions: NoticeDropdownOption[] = [
    {
      id: NoticeType.INFORMATION,
      text: 'SYSTEM_SETTINGS.NOTICES.TYPE_INFORMATION'
    },
    {
      id: NoticeType.WARNING,
      text: 'SYSTEM_SETTINGS.NOTICES.TYPE_WARNING'
    },
    {
      id: NoticeType.ALERT,
      text: 'SYSTEM_SETTINGS.NOTICES.TYPE_ALERT'
    }
  ];

  noticePlacementOptions: NoticeDropdownOption[] = [
    {
      id: NoticePlacement.SALE_ADD_PRODUCT_IDENTIFIER_SN,
      text: 'SYSTEM_SETTINGS.NOTICES.PLACEMENT_SALE_IDENTIFIER'
    },
    {
      id: NoticePlacement.WARRANTY_ACTIVATION_IDENTIFIER_SN,
      text: 'SYSTEM_SETTINGS.NOTICES.PLACEMENT_WARRANTY_IDENTIFIER'
    },
    {
      id: NoticePlacement.DIGITAL_PASSPORT_HEADER,
      text: 'SYSTEM_SETTINGS.NOTICES.PLACEMENT_DIGITAL_PASSPORT_HEADER'
    },
    {
      id: NoticePlacement.DIGITAL_PASSPORT_FOOTER,
      text: 'SYSTEM_SETTINGS.NOTICES.PLACEMENT_DIGITAL_PASSPORT_FOOTER'
    }
  ];

  // Quill toolbar config (https://quilljs.com/docs/modules/toolbar)
  editorModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean']
    ]
  };

  noticeForm: FormGroup = this.fb.group({
    name: ['', Validators.required],
    type: [[], Validators.required],
    placement: [[], Validators.required],
    translations: this.fb.array([])
  });

  constructor(
    private settingsService: SettingsService,
    private fb: FormBuilder,
    private sessionService: SessionService
  ) { }

  async ngOnInit(): Promise<void> {
    await this.getNotices();
  }

  get translationForms(): FormArray {
    return this.noticeForm.controls['translations'] as FormArray;
  }

  get allPlacementsAssigned(): boolean {
    return this.totalRecords >= this.noticePlacementOptions.length;
  }

  get hasNoticeTranslation(): boolean {
    for (const control of this.translationForms.controls) {
      const translation = control.get('translation')?.value;

      if (!translation) {
        continue;
      }

      try {
        const content = JSON.parse(translation);

        for (const operation of content.ops) {
          // Quill stores text in insert
          const hasText = typeof operation.insert === 'string' && operation.insert.trim().length > 0;

          if (hasText) {
            return true;
          }
        }
      } catch {
        continue;
      }
    }

    return false;
  }

  private async getNotices(): Promise<void> {
    this.loading = true;

    const payload = {
      pageNumber: this.getPageNumber(),
      pageSize: this.paginatorRows
    };

    try {
      const response = await firstValueFrom(this.settingsService.apiSettingsNoticesFilterTablePost(payload));

      this.paginatedNotices = response.results ?? [];
      this.totalRecords = response.totalCount ?? this.paginatedNotices.length;
    } catch (error) {
      console.error('Error fetching notices:', error);

      this.paginatedNotices = [];
      this.totalRecords = 0;
    } finally {
      this.loading = false;
    }
  }

  async onPageChange(event: { first: number; rows: number }): Promise<void> {
    this.paginatorFirst = event.first;
    this.paginatorRows = event.rows;

    await this.getNotices();
  }

  private getPageNumber(): number {
    return Math.floor(this.paginatorFirst / this.paginatorRows) + 1;
  }

  getNoticeTypeLabel(type: string | undefined): string {
    const id = this.toTypeId(type);
    return id == null ? '' : this.noticeTypeLabels[id] ?? '';
  }

  getNoticePlacementLabel(placement: string | undefined): string {
    const id = this.toPlacementId(placement);
    return id == null ? '' : this.noticePlacementLabels[id] ?? '';
  }

  async onStateChange(event: { checked: boolean }, notice: NoticeTableResponse): Promise<void> {
    if (notice.id == null || this.updatingNoticeId != null) {
      return;
    }

    const previousValue = notice.isActive;
    const newValue = event.checked;

    // pending request (so the template can disable the switches)
    this.updatingNoticeId = notice.id;

    try {
      if (newValue) {
        await firstValueFrom(this.settingsService.apiSettingsNoticesIdActivatePatch(notice.id));
      } else {
        await firstValueFrom(this.settingsService.apiSettingsNoticesIdDeactivatePatch(notice.id));
      }

      // update row
      notice.isActive = newValue;
    } catch (error) {
      console.error('Error changing notice state:', error);
      notice.isActive = previousValue;
    } finally {
      this.updatingNoticeId = null;
    }
  }

  async openNoticeModalbox(noticeId: number | null = null): Promise<void> {
    if (this.openingNoticeModal || this.savingNotice || this.updatingNoticeId != null) {
      return;
    }

    this.openingNoticeModal = true;

    try {
      const settings = await this.sessionService.getCompanySettings();
      const notices = this.paginatedNotices

      // create wysiwyg editos for enabled langs
      this.enabledLanguages = [];
      for (const language of settings.languages ?? []) {
        if (language.isEnabled && language.code) {
          this.enabledLanguages.push({
            code: language.code,
            name: language.name || language.code
          });
        }
      }

      const assignedPlacements = notices
        .filter(notice => notice.id !== noticeId)
        .map(notice => this.toPlacementId(notice.placement));

      // remove placements used in other notices (inc inactives)
      this.availablePlacementOptions = this.noticePlacementOptions.filter(option => {
        const isAssigned = assignedPlacements.some(placement => {
          return placement === option.id;
        });

        return !isAssigned;
      });

      // refresh if creation is no longer possible
      if (noticeId == null && this.availablePlacementOptions.length === 0) {
        await this.getNotices();
        return;
      }

      this.selectedNoticeId = noticeId;
      this.existingTranslations = [];
      this.translationForms.clear();

      this.noticeForm.reset({
        name: '',
        type: [],
        placement: []
      });

      // load saved values
      if (noticeId != null) {
        const notice = await firstValueFrom(
          this.settingsService.apiSettingsNoticesDetailsIdGet(noticeId)
        );

        this.existingTranslations = notice.noticeTranslations ?? [];

        this.noticeForm.patchValue({
           name: notice.name ?? '',
           type: notice.type == null ? [] : [this.toTypeId(notice.type)],
           placement: notice.placement == null ? [] : [this.toPlacementId(notice.placement)]
        });
      }

      // add one editor control per enabled lang and load its data
      for (const language of this.enabledLanguages) {
        const existingTranslation = this.existingTranslations.find(translation => {
          return translation.isoCode?.toLowerCase() === language.code.toLowerCase();
        });

        this.translationForms.push(
          this.fb.group({
            isoCode: [language.code],
            translation: [existingTranslation?.translation || null]
          })
        );
      }

      this.showNoticeModalbox = true;
    } catch (error) {
      console.error('Error opening notice:', error);
    } finally {
      this.openingNoticeModal = false;
    }
  }

  closeNoticeModalbox(): void {
    this.showNoticeModalbox = false;
    this.selectedNoticeId = null;
    this.existingTranslations = [];
    this.translationForms.clear();

    this.noticeForm.reset({
      name: '',
      type: [],
      placement: []
    });
  }

  goToDelete(event: any, data: any) {
    this.currentNotice = data;
    this.currentNoticeName = data?.name;
    this.showDeleteModal = true;
  }

  async onDelete(): Promise<void> {
    try {
      await firstValueFrom(
        this.settingsService.apiSettingsNoticesDeleteIdDelete(this.currentNotice.id)
      );

      this.showDeleteModal = false;
      await this.getNotices();
    } catch (error) {
      console.error(error);
    }
  }

  onCancelDelete(): void {
    this.showDeleteModal = false;
  }

  private buildNoticeRequest(): NoticeRequest {
    const translations = this.translationForms.controls.map(control => {
      return {
        isoCode: control.get('isoCode')!.value as string,
        translation: (control.get('translation')!.value || '') as string
      };
    });

    const enabledLanguageCodes = this.enabledLanguages.map(language => {
      return language.code.toLowerCase();
    });

    // preserve saved content for disabled langs
    for (const translation of this.existingTranslations) {
      if (!translation.isoCode) {
        continue;
      }

      const isEnabledLanguage = enabledLanguageCodes.includes(
        translation.isoCode.toLowerCase()
      );

      if (!isEnabledLanguage) {
        translations.push({
          isoCode: translation.isoCode,
          translation: translation.translation ?? ''
        });
      }
    }

    return {
      name: this.noticeForm.controls['name'].value.trim(),
      type: NoticeType[this.noticeForm.controls['type'].value[0]] as NoticeRequest['type'],
      placement: NoticePlacement[this.noticeForm.controls['placement'].value[0]] as NoticeRequest['placement'],
      noticeTranslations: translations
    };
  }
  
  private toPlacementId(value?: string | null): number | null {
  if (!value) return null;
  return NoticePlacement[value as keyof typeof NoticePlacement] ?? null;
  } 

  private toTypeId(value?: string | null): number | null {
    if (!value) return null;
    return NoticeType[value as keyof typeof NoticeType] ?? null;
  }

  async saveNotice(): Promise<void> {
    if (this.savingNotice) {
      return;
    }

    const nameControl = this.noticeForm.controls['name'];

    if (!nameControl.value?.trim()) {
      nameControl.setErrors({ required: true });
    }

    if (this.noticeForm.invalid || this.enabledLanguages.length === 0 || !this.hasNoticeTranslation) {
      this.noticeForm.markAllAsTouched();
      return;
    }

    const payload = this.buildNoticeRequest();

    this.savingNotice = true;
    this.noticeForm.disable();

    try {
      if (this.selectedNoticeId != null) {
        await firstValueFrom(this.settingsService.apiSettingsNoticesUpdateIdPut(this.selectedNoticeId, payload));
      } else {
        await firstValueFrom(this.settingsService.apiSettingsNoticesCreatePost(payload));

        this.paginatorFirst = 0;
      }

      this.closeNoticeModalbox();
      await this.getNotices();
    } catch (error) {
      console.error('Error saving notice:', error);
    } finally {
      this.noticeForm.enable();
      this.savingNotice = false;
    }
  }
}
